import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  Package,
  ArrowLeftRight,
  FileText,
  Warehouse,
  Shield,
  LogOut,
  TrendingUp,
  Calendar,
  Activity,
  UserPlus,
  Crown,
  UserMinus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StockCheckLogo } from "@/components/Logo";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) {
      navigate("/admin/login");
      return;
    }
    fetchAdminData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const adminToken = localStorage.getItem("adminToken");
    return { Authorization: `Bearer ${adminToken}` };
  };

  const fetchAdminData = async () => {
    const headers = getAuthHeaders();

    try {
      const [statsRes, usersRes, activityRes, chartRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/users?limit=20`, { headers }),
        fetch(`${API_URL}/api/admin/activity?limit=20`, { headers }),
        fetch(`${API_URL}/api/admin/signups-chart?days=14`, { headers }),
      ]);

      if (!statsRes.ok) throw new Error("Failed to fetch admin data");

      setStats(await statsRes.json());
      setUsers(await usersRes.json());
      setActivity(await activityRes.json());
      setChartData(await chartRes.json());
    } catch (error) {
      console.error("Admin fetch error:", error);
      toast.error("Failed to load admin data");
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteUser = async (email) => {
    setActionLoading(email);
    const headers = getAuthHeaders();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/promote`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to promote user");
      }
      
      toast.success(data.message);
      // Refresh user list
      fetchAdminData();
    } catch (error) {
      toast.error(error.message || "Failed to promote user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDemoteUser = async (email) => {
    setActionLoading(email);
    const headers = getAuthHeaders();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/demote`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to demote user");
      }
      
      toast.success(data.message);
      // Refresh user list
      fetchAdminData();
    } catch (error) {
      toast.error(error.message || "Failed to demote user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("admin");
    toast.success("Logged out from admin");
    navigate("/admin/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-center">
          <StockCheckLogo size="large" showText={true} />
          <div className="mt-8 flex items-center justify-center gap-3">
            <span className="animate-spin w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full" />
            <span className="text-slate-400">Loading admin dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  // Get current admin email from localStorage
  const currentAdmin = JSON.parse(localStorage.getItem("admin") || "{}");
  const currentAdminEmail = currentAdmin.email;

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      {/* Header */}
      <header className="bg-[#151923] border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <StockCheckLogo size="default" showText={true} />
            <div className="flex items-center gap-2 bg-red-500/10 px-3 py-1 rounded-full">
              <Shield className="w-4 h-4 text-red-500" />
              <span className="text-red-500 text-sm font-medium">Admin Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">{currentAdminEmail}</span>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Total Users"
            value={stats?.total_users || 0}
            color="blue"
          />
          <StatCard
            icon={UserPlus}
            label="Today"
            value={stats?.signups_today || 0}
            color="green"
          />
          <StatCard
            icon={TrendingUp}
            label="Last 7 Days"
            value={stats?.recent_signups_7d || 0}
            color="purple"
          />
          <StatCard
            icon={Package}
            label="Products"
            value={stats?.total_products || 0}
            color="yellow"
          />
          <StatCard
            icon={ArrowLeftRight}
            label="Transactions"
            value={stats?.total_transactions || 0}
            color="cyan"
          />
          <StatCard
            icon={FileText}
            label="Invoices"
            value={stats?.total_invoices || 0}
            color="pink"
          />
          <StatCard
            icon={Warehouse}
            label="Warehouses"
            value={stats?.total_warehouses || 0}
            color="orange"
          />
        </div>

        {/* Sign-ups Chart */}
        <div className="bg-[#151923] border border-slate-800 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Sign-ups (Last 14 Days)
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#64748B"
                  tick={{ fill: "#64748B", fontSize: 12 }}
                  tickFormatter={(val) => val.slice(5)}
                />
                <YAxis
                  stroke="#64748B"
                  tick={{ fill: "#64748B", fontSize: 12 }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1E293B",
                    border: "1px solid #334155",
                    borderRadius: "8px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="signups"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* User Management */}
          <div className="bg-[#151923] border border-slate-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-green-400" />
              User Management
            </h2>
            <div className="space-y-3 max-h-[450px] overflow-y-auto">
              {users.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No users yet</p>
              ) : (
                users.map((user) => (
                  <div
                    key={user.user_id}
                    className="flex items-center justify-between p-3 bg-[#0B0E14] rounded-lg"
                    data-testid={`user-row-${user.email}`}
                  >
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.role === "admin" ? "bg-red-600" : "bg-blue-600"
                        }`}>
                          <span className="text-white font-medium">
                            {user.name?.[0]?.toUpperCase() || "?"}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{user.name}</p>
                          {user.role === "admin" && (
                            <Crown className="w-4 h-4 text-yellow-400" />
                          )}
                        </div>
                        <p className="text-slate-500 text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex gap-1">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              user.role === "admin"
                                ? "bg-red-500/20 text-red-400"
                                : "bg-slate-500/20 text-slate-400"
                            }`}
                          >
                            {user.role}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              user.auth_provider === "google"
                                ? "bg-blue-500/20 text-blue-400"
                                : "bg-slate-500/20 text-slate-400"
                            }`}
                          >
                            {user.auth_provider}
                          </span>
                        </div>
                        <p className="text-slate-500 text-xs">
                          {formatDate(user.created_at)}
                        </p>
                      </div>
                      {/* Admin action buttons */}
                      {user.email !== currentAdminEmail && (
                        <div className="ml-2">
                          {user.role === "admin" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDemoteUser(user.email)}
                              disabled={actionLoading === user.email}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              title="Remove admin"
                              data-testid={`demote-btn-${user.email}`}
                            >
                              {actionLoading === user.email ? (
                                <span className="animate-spin w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full" />
                              ) : (
                                <UserMinus className="w-4 h-4" />
                              )}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePromoteUser(user.email)}
                              disabled={actionLoading === user.email}
                              className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                              title="Make admin"
                              data-testid={`promote-btn-${user.email}`}
                            >
                              {actionLoading === user.email ? (
                                <span className="animate-spin w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full" />
                              ) : (
                                <Crown className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-[#151923] border border-slate-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              Recent Activity
            </h2>
            <div className="space-y-3 max-h-[450px] overflow-y-auto">
              {activity.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No activity yet</p>
              ) : (
                activity.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-[#0B0E14] rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          item.type === "transaction"
                            ? item.subtype === "in"
                              ? "bg-green-500/20"
                              : "bg-red-500/20"
                            : "bg-blue-500/20"
                        }`}
                      >
                        {item.type === "transaction" ? (
                          <ArrowLeftRight
                            className={`w-5 h-5 ${
                              item.subtype === "in" ? "text-green-400" : "text-red-400"
                            }`}
                          />
                        ) : (
                          <FileText className="w-5 h-5 text-blue-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white text-sm">{item.description}</p>
                        <p className="text-slate-500 text-xs">
                          {item.details?.warehouse || item.details?.supplier_customer || ""}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">
                        ${item.value?.toLocaleString() || 0}
                      </p>
                      <p className="text-slate-500 text-xs">
                        {formatDate(item.timestamp)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: "bg-blue-500/20 text-blue-400",
    green: "bg-green-500/20 text-green-400",
    purple: "bg-purple-500/20 text-purple-400",
    yellow: "bg-yellow-500/20 text-yellow-400",
    cyan: "bg-cyan-500/20 text-cyan-400",
    pink: "bg-pink-500/20 text-pink-400",
    orange: "bg-orange-500/20 text-orange-400",
    red: "bg-red-500/20 text-red-400",
  };

  return (
    <div className="bg-[#151923] border border-slate-800 rounded-lg p-4">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-slate-500 text-sm">{label}</p>
    </div>
  );
}
